import { useEffect, useRef, useState } from "react";
import { unwrap } from "../api/scoreApi";

// #2251's Class.testDisagreement (score-api's computeTestDisagreement,
// apps/score-api/src/handlers/daysheet.js) only ever compares against a
// class's own REAL bound testId — never against the fvd-unmatched:<n>
// fallback sentinel a class/ride gets when FVD's test string didn't resolve
// to a catalog def. This mirrors that same one-line predicate
// (apps/score-api/src/lib/catalog-enrich.js:492, isRealTestSlug) so the FE
// doesn't list an unresolved sentinel id next to a real test name — FE has
// no import path into API-only code, so this is a deliberate duplicate, not
// a fork; a change to the backend predicate needs the same change here.
function isRealTestSlug(slug) {
  return typeof slug === "string" && slug.length > 0 && !slug.startsWith("fvd-unmatched:");
}

// #2252 — Class.testDisagreement (#2251) records only THAT a class and its
// rides disagree, not WHICH test ids disagree (see the field's own doc
// comment, packages/score-common/src/constants.js). Naming both sides for
// the operator means asking each flagged class's own rides what test they
// actually resolve to. GET /ride?classId=... is the same per-class call
// ClassDetailDrawer already makes (apps/scoreadmin-fe/src/components/
// ClassDetailDrawer.jsx) — scoped to ONE class, never the whole show's
// rides. Only classIds that are ACTUALLY flagged are ever passed in here
// (see ClassManager's flaggedClassIds), so in the overwhelmingly common
// case — no flagged classes on the show — this hook fires zero requests.
//
// Returns a plain object keyed by classId -> array of distinct real test
// slugs found across that class's rides (may include a slug equal to the
// class's own test, per card #2252's class-814 example: a class can have
// some rides on the correct test and others on a different one, so the
// full set is what the operator needs to see, not just the mismatched
// slugs). A classId not yet resolved (still loading, or the fetch failed)
// is simply absent from the returned object.
export function useTestDisagreementRideTests(classIds, scoreApi) {
  const [byClassId, setByClassId] = useState({});
  const fetchedRef = useRef(new Set());

  useEffect(() => {
    const toFetch = (classIds || []).filter((id) => id && !fetchedRef.current.has(id));
    if (!toFetch.length) return undefined;
    toFetch.forEach((id) => fetchedRef.current.add(id));
    let cancelled = false;
    toFetch.forEach((classId) => {
      scoreApi.rides
        .listRides(classId)
        .then(unwrap)
        .then((rides) => {
          if (cancelled) return;
          const slugs = [...new Set((rides || []).map((r) => r?.testId).filter(isRealTestSlug))];
          setByClassId((prev) => ({ ...prev, [classId]: slugs }));
        })
        .catch(() => {
          // Non-fatal: a class stays flagged (the backend field is still
          // true) even if this detail fetch fails — the chip still shows,
          // just without the ride-side test names.
          fetchedRef.current.delete(classId);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [classIds, scoreApi]);

  return byClassId;
}
